import DataStore from './data-store'
import StuartService from '../services/stuart-service';
import ShopService from '../services/shop-service';
import _ from 'lodash';
import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_API_KEY } from './constants'
import { Address, DeliveryZone, Shop } from '../../types';

export default {
    loadGoogle,
    checkAddressAsync,
    addressFromPlace,
    geocode,
    getZoneAsync,
    checkZonesAsync,
    formattedAddress,
    getMapStyles,
    getLocationAsync,
    testAddress,
}

let googlePromise: Promise<any>;
loadGoogle();

function testAddress(address: Address, zone: DeliveryZone) {

}

function getFromAngular(provider: string) {
	let injector = window.injector;
	if (!injector) return null;
	return injector.get(provider);
}

function loadGoogle() {
    if (!googlePromise) {
        let gmLibrary = null && getFromAngular('gmLibrary');
        if (!gmLibrary) {
            googlePromise = new Loader({
                apiKey: GOOGLE_API_KEY,
                version: "weekly",
                libraries: ["places"],
            }).load();
        } else {
            googlePromise = new Promise((resolve, reject) => {
                gmLibrary.load().then(() => resolve(window.google))
                .catch(err => reject(err));
            });
        }
    }
    return googlePromise;
}

function formattedAddress(address: Address) {
    return _.trim(`${address.PlusCode||''} ${address.Address||''}, ${address.Zip||''} ${address.City||''}, ${address.Country||''}`,' ,');
}

function geocode(request: google.maps.GeocoderRequest) {
    return new Promise<google.maps.GeocoderResult[]>((resolve,reject) => {
        new google.maps.Geocoder().geocode(request, (places, status) => {
            if (status == 'OK' || status == 'ZERO_RESULTS') {
                resolve(places);
            } else {
                reject(places);
            }
            DataStore.notifyAngularJs();
        });
    });
}

function getFromPlace(place: google.maps.GeocoderResult,address_part: string) {
    for(let i in place.address_components) {
        let component = place.address_components[i];
        if (component.types.indexOf(address_part) != -1) {
            return component.long_name
        }
    }
    return '';
}

function addressFromPlace(place: google.maps.GeocoderResult) {
    let latlng = place.geometry.location.toJSON();
    return place && {
        place: place,
        latlng,
        Location: { latitude: latlng.lat, longitude: latlng.lng },
        PlusCode: getFromPlace(place, 'plus_code'),
        Address: [getFromPlace(place,'street_number'), getFromPlace(place,'route') ].join(' ').trim(),
        City: getFromPlace(place,'locality'),
        Country: getFromPlace(place,'country'),
        Zip: getFromPlace(place,'postal_code'),
    }
}

function checkAddressAsync(order: Order, address: Address) {
    let promise = Promise.resolve();
    if (StuartService.needsStuartJob(order)) {
        promise = StuartService.checkAddressAsync(order, address);
    }
    return promise.then(() => {
        if (StuartService.needZone(order)) {
            return getZoneAsync(order.shop, address)
            .then(zone => {
                if (!zone) {
                    return Promise.reject("Adresse non couverte par le service de livraison");
                }
                return address;
            })
        } else {
            return address;
        }
    })
}

// checkZones() returns checks against all zones
// if shortcut == true, return as soon as one zone matches
// if false, checks against all zones
function checkZonesAsync(shop: Shop, address: Address, shortcut):
Promise<{ status: 'OK'|'KO', msg?: string, zone: DeliveryZone, zoneToMatch?: boolean, distance?: number }[]> {
    let zones = shop.delivery_zones;
    let results = [];
    for(let zone of zones) {
        let zoneShop = getShopForZone(shop, zone);
        if (!ShopService.hasSaleArea(zoneShop, ShopService.SALE_MODES.CLIENT, ShopService.SALE_AREAS.DELIVERY)) {
            results.push({ status: 'KO', zone, msg: 'Livraison inactive pour cet établissement' });
        } else if (zone.ZoneType == 'Zip' || !zone.ZoneType) {
            // Zip matches address Zip or address city
            if (address.Zip == zone.Zip) {
                results.push({status:'OK', zone})
                if (shortcut) {
                    return Promise.resolve(results);
                }
            } else if ((address.City || '').toLowerCase() == zone.Zip.toLowerCase()) {
                results.push({status:'OK', zone})
                if (shortcut) {
                    return Promise.resolve(results);
                }
            }
        } else if (zone.ZoneType == 'Distance' && zoneShop) {
            // will change to OK if its the shortest distance
            results.push({ status: 'KO', zone, zoneToMatch: true })
        }
    }

    // need to compute distances and return them
    let shopsToMatch = _(results).filter(r=>r.zoneToMatch).map(r=>({shop: getShopForZone(shop, r.zone)})).keyBy(r2=>r2.shop.ShopId).value();
    if (_.isEmpty(shopsToMatch)) {
        return Promise.resolve(results);
    }
    return computeDistanceAsync(address, shopsToMatch).then(distances => {
        if (!distances.length) {
            return results;
        }
        for(let distance of distances) {
            for(let result of results) {
                if (result.zoneToMatch && result.zone.ShopId == distance.ShopId) {
                    result.distance = distance.km;
                }
            }
        }
        return results;
    })
}

function getShopForZone(shop: Shop, zone: DeliveryZone): Shop {
    return _.find(shop.sister_shops,(sister)=>sister.ShopId == zone.ShopId);
}

function getZoneAsync(shop: Shop, address: Address, shortcut = true) {
    return checkZonesAsync(shop, address, shortcut)
    .then((results) => {
        for(let result of results) {
            if (result.status == 'OK') {
                return result.zone;
            }
        }

        // not there yet: need to return the shortest distance to matching zone
        // 1 filter too far zones
        // 2 find the closest zone
        // 3 order by the smallest zone distance defined (2km and 1km max => select the 1km zone)
        let nearestZoneResult = _(results).filter(r=>r.zoneToMatch).filter(r => r.zone.Distance >= r.distance).minBy(r => r.zone.Distance);
        return nearestZoneResult?.zone;
    })
}

function getLocationAsync(address: Address) {
	if (address.Location) {
		return Promise.resolve(address);
	}
	if (address.latlng) {
		address.Location = {
			latitude: _.isNumber(address.latlng.lat)?address.latlng.lat:address.latlng.lat(),
			longitude: _.isNumber(address.latlng.lng)?address.latlng.lng:address.latlng.lng(),
		};
        address.isModified = true;
		return Promise.resolve(address);
	}
	// ask google
	return geocode({address: formattedAddress(address)}).then(places => {
		if (places[0]) {
			address.Location = {
				latitude: places[0].geometry.location.lat(),
				longitude: places[0].geometry.location.lng(),
			};
            address.isModified = true;
		}
		return address;
	});
}

function computeDistanceAsync(address, shopsToMatch): Promise<Array<{km: number, ShopId: number}>> {
    return getLocationAsync(address).then((address) => {
        let shopIds = _.keys(shopsToMatch)
        let destinations = _.map(shopsToMatch,(ztm)=> ({ lat: ztm.shop.Location?.latitude, lng: ztm.shop.Location?.longitude }));
        var service = new window.google.maps.DistanceMatrixService();
        return new Promise((resolve,reject) => {
            let request = {
                origins: [{ lat: address.Location.latitude, lng: address.Location.longitude }],
                destinations: destinations,
                travelMode: window.google.maps.TravelMode.DRIVING,
            };
            service.getDistanceMatrix(request, (response, status) => {
                let distances = [];
                let elements = response.rows[0].elements;
                for(let i in elements) {
                    if (elements[i].status == "ZERO_RESULTS") {
                        continue;
                    }
                    let km = elements[i].distance.value / 1000.;
                    distances.push({
                        km: km,
                        ShopId: shopIds[i],
                    });
                }
                resolve(distances);
            });
        });
    });
}

function getMapStyles() {
    return [
        {
            "featureType": "administrative",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "administrative.country",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "administrative.country",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#6b6b6b"
                }
            ]
        },
        {
            "featureType": "administrative.province",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "administrative.locality",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "administrative.neighborhood",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "administrative.neighborhood",
            "elementType": "geometry",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "landscape.natural",
            "elementType": "geometry.fill",
            "stylers": [
                {
                    "visibility": "on"
                },
                {
                    "color": "#e0efef"
                }
            ]
        },
        {
            "featureType": "poi",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "poi",
            "elementType": "geometry.fill",
            "stylers": [
                {
                    "visibility": "on"
                },
                {
                    "hue": "#1900ff"
                },
                {
                    "color": "#c0e8e8"
                }
            ]
        },
        {
            "featureType": "poi.business",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "poi.government",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "poi.medical",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "poi.park",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "poi.place_of_worship",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "poi.school",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "road",
            "elementType": "geometry",
            "stylers": [
                {
                    "lightness": 100
                },
                {
                    "visibility": "simplified"
                }
            ]
        },
        {
            "featureType": "road",
            "elementType": "labels",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "road.local",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "transit.line",
            "elementType": "geometry",
            "stylers": [
                {
                    "visibility": "on"
                },
                {
                    "lightness": 700
                }
            ]
        },
        {
            "featureType": "transit.station.bus",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "simplified"
                }
            ]
        },
        {
            "featureType": "transit.station.rail",
            "elementType": "all",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "water",
            "elementType": "all",
            "stylers": [
                {
                    "color": "#7dcdcd"
                }
            ]
        }
    ];
}